import { useCallback, useState } from "react";
import {
	ContextMenu,
	type ContextMenuEntry,
	type ContextMenuItem,
	type ContextMenuPosition,
} from "@/components/common/ContextMenu";
import { DisclosureChevron } from "@/components/common/Disclosure";
import { FileIcon } from "@/components/common/FileIcon";
import { Spinner } from "@/components/common/Spinner";
import { NewFileDialog } from "@/components/file-tree/NewFileDialog";
import { NewFolderDialog } from "@/components/file-tree/NewFolderDialog";
import {
	TREE_INDENT_PX,
	TREE_ROOT_PADDING_PX,
	TreeIndentGuides,
} from "@/components/file-tree/TreeIndentGuides";
import { buildFileTypeActions } from "@/components/file-tree/file-type-actions";
import { useEnvironmentUpload } from "@/components/file-tree/useEnvironmentUpload";
import { environmentFileContentUrl } from "@/lib/api";
import { canDropEnvironmentEntries } from "@/lib/dnd/environment-entry-drop";
import type { EnvironmentEntryPayload } from "@/lib/dnd/payload";
import { useDraggable } from "@/lib/dnd/useDraggable";
import { useDropTarget } from "@/lib/dnd/useDropTarget";
import {
	createFileInEnvironment,
	createFolderInEnvironment,
	splitPath,
} from "@/lib/environment-files";
import { downloadFile } from "@/lib/download-file";
import { copyPathToClipboard, insertPathIntoMessage } from "@/lib/path-actions";
import { problemIndicatorFor } from "@/lib/problems";
import { TREE_ROW_DRAGOVER_CLASS, ROW_HOVER_CLASS, ROW_SELECTED_CLASS } from "@/lib/row-styles";
import {
	selectCanInsertPathIntoMessage,
	selectEffectiveEnvironmentId,
	useChatStore,
} from "@/stores/useChatStore";
import { useDiagnosticsStore } from "@/stores/useDiagnosticsStore";
import { useToastStore } from "@/stores/useToastStore";
import { getFileIconType } from "@/lib/file-type";
import type { TreeEntry } from "./useFileTree";

interface FileTreeNodeProps {
	entry: TreeEntry;
	depth: number;
	expanded: boolean;
	/** 直下を取得中か (遅延読み込み中のフォルダ)。 */
	loading?: boolean;
	isCurrent: boolean;
	selectedPaths: ReadonlySet<string>;
	onSelect: (path: string, event: React.MouseEvent) => void;
	onContextSelect: (path: string) => void;
	onToggleExpand: (path: string, expanded: boolean) => void;
	onRowKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>, path: string) => void;
	onRequestRename: (entry: TreeEntry) => void;
	createDragPayload: (entry: TreeEntry) => EnvironmentEntryPayload;
	onMoveEntries: (payload: EnvironmentEntryPayload, destinationPath: string) => void;
	onDropOsFiles: (dataTransfer: DataTransfer, destinationDirPath: string) => void;
	/** OS からのファイルドロップを受け入れられるか (環境未選択・アップロード進行中は false)。 */
	osDropEnabled: boolean;
	onDownloadSelected: () => void;
	onDeleteSelected: () => void;
}

export function FileTreeNode({
	entry,
	depth,
	expanded,
	loading = false,
	isCurrent,
	selectedPaths,
	onSelect,
	onContextSelect,
	onToggleExpand,
	onRowKeyDown,
	onRequestRename,
	createDragPayload,
	onMoveEntries,
	onDropOsFiles,
	osDropEnabled,
	onDownloadSelected,
	onDeleteSelected,
}: FileTreeNodeProps) {
	const [menuPosition, setMenuPosition] = useState<ContextMenuPosition | null>(null);
	const [newFolderOpen, setNewFolderOpen] = useState(false);
	const [newFileOpen, setNewFileOpen] = useState(false);
	const effectiveEnvironmentId = useChatStore(selectEffectiveEnvironmentId);
	const isDir = entry.type === "directory";
	// 新規作成・アップロードの対象ディレクトリ。ディレクトリは自身、ファイルは親ディレクトリ。
	const createParentPath = isDir ? entry.path : splitPath(entry.path).dir;
	const { uploadInputs, uploadMenuItems } = useEnvironmentUpload(
		createParentPath,
		"file-node-upload",
		{ disabled: effectiveEnvironmentId === null },
	);
	const fileId = isDir ? null : entry.fileId;
	const isSelected = selectedPaths.has(entry.path);
	const chatId = useChatStore((s) => s.chatId);
	const openFile = useChatStore((s) => s.openFile);
	const openFilePreview = useChatStore((s) => s.openFilePreview);
	const canInsertPathIntoMessage = useChatStore(selectCanInsertPathIntoMessage);
	const diagnostics = useDiagnosticsStore((s) =>
		fileId !== null ? s.byPath[entry.path] : undefined,
	);
	const { total: totalProblems, color: problemColor } = problemIndicatorFor(diagnostics);

	const dragProps = useDraggable(createDragPayload(entry));
	const { isOver, dropProps } = useDropTarget({
		accept: "environment-entry",
		canDrop: (payload) => isDir && canDropEnvironmentEntries(payload, entry.path),
		onDrop: (payload) => onMoveEntries(payload, entry.path),
		acceptFiles: {
			/** ファイル行へのドロップは、その親ディレクトリへのアップロードとして扱う。 */
			canDrop: () => osDropEnabled,
			onDrop: (e) => onDropOsFiles(e.dataTransfer, createParentPath),
		},
	});

	const handleClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.currentTarget.focus({ preventScroll: true });
			onSelect(entry.path, event);
			if (event.metaKey || event.ctrlKey) return;
			if (fileId === null) onToggleExpand(entry.path, !expanded);
			else openFilePreview(fileId);
		},
		[entry.path, expanded, fileId, onSelect, onToggleExpand, openFilePreview],
	);

	const handleContextMenu = useCallback(
		(event: React.MouseEvent) => {
			event.preventDefault();
			// ノード上の右クリックはペインの空白メニューを開かないよう伝播を止める。
			event.stopPropagation();
			onContextSelect(entry.path);
			if (fileId !== null) openFilePreview(fileId);
			setMenuPosition({ x: event.clientX, y: event.clientY });
		},
		[entry.path, fileId, onContextSelect, openFilePreview],
	);

	const handleCopyPath = useCallback(() => copyPathToClipboard(`~/${entry.path}`), [entry.path]);

	const handleCopyPermalink = useCallback(async () => {
		if (fileId === null || chatId === null) return;
		// FileUrlSync が復元する `file` クエリと同じ形式。開くとこのファイルのタブが復元される。
		const url = new URL(`/chats/${chatId}`, window.location.origin);
		url.searchParams.set("file", fileId);
		await navigator.clipboard.writeText(url.toString());
		useToastStore.getState().show({ type: "success", message: "固定リンクをコピーしました。" });
	}, [chatId, fileId]);

	const handleAddPathToInput = useCallback(
		() => insertPathIntoMessage(`~/${entry.path}`),
		[entry.path],
	);

	const handleDownload = useCallback(() => {
		if (fileId === null) return;
		const environmentId = selectEffectiveEnvironmentId(useChatStore.getState());
		if (environmentId === null) return;
		// content エンドポイントは決定的に組み立てられるため取得のラウンドトリップは不要。
		// download=true で Content-Disposition: attachment になり、保存ダイアログにファイル名が付く。
		downloadFile(environmentFileContentUrl(environmentId, fileId, { download: true }));
	}, [fileId]);

	const createEntryInParent = useCallback(
		async (
			create: (environmentId: string, parentPath: string, name: string) => Promise<void>,
			name: string,
		) => {
			const environmentId = selectEffectiveEnvironmentId(useChatStore.getState());
			if (environmentId === null) return;
			await create(environmentId, createParentPath, name);
			// ルート直下は展開の概念がないため、パスがあるときだけ展開する。
			if (createParentPath) onToggleExpand(createParentPath, true);
		},
		[createParentPath, onToggleExpand],
	);

	// 複数選択と単一ディレクトリは配下を含む ZIP、単一ファイルは直接ダウンロード。
	const downloadItem: ContextMenuItem =
		selectedPaths.size > 1 || isDir
			? {
					label: "zip一括ダウンロード",
					testId: "file-node-menu-download",
					onSelect: onDownloadSelected,
				}
			: { label: "ダウンロード", testId: "file-node-menu-download", onSelect: handleDownload };

	// 種別固有の操作 (アーカイブの展開など) はこのノード単体に作用するため、単一選択のときだけ出す。
	const fileTypeActions =
		entry.type === "file" && selectedPaths.size <= 1
			? buildFileTypeActions({ entry, environmentId: effectiveEnvironmentId, onToggleExpand })
			: [];

	const menuItems: ContextMenuEntry[] = [
		{
			label: "新規ファイル",
			testId: "file-node-menu-new-file",
			onSelect: () => setNewFileOpen(true),
		},
		{
			label: "新規フォルダ",
			testId: "file-node-menu-new-folder",
			onSelect: () => setNewFolderOpen(true),
		},
		{ separator: true },
		...uploadMenuItems,
		downloadItem,
		...fileTypeActions,
		{ separator: true },
		{ label: "パスをコピー", testId: "file-node-menu-copy-path", onSelect: handleCopyPath },
		// 固定リンクはファイルタブを復元する URL なので、ファイルかつチャットが確定しているときだけ出す。
		...(fileId !== null && chatId !== null
			? [
					{
						label: "固定リンクをコピー",
						testId: "file-node-menu-copy-permalink",
						onSelect: handleCopyPermalink,
					},
				]
			: []),
		...(canInsertPathIntoMessage
			? [
					{
						label: "メッセージにパスを追加",
						testId: "file-node-menu-add-path",
						onSelect: handleAddPathToInput,
					},
				]
			: []),
		{ separator: true },
		// 名前変更はこのノード単体にしか作用しないため、複数選択中はメニューから外す。
		// 表示したまま実行すると、選択中の全件が処理されたと誤解されるおそれがある。
		...(selectedPaths.size <= 1
			? [
					{
						label: "名前を変更",
						testId: "file-node-menu-rename",
						onSelect: () => onRequestRename(entry),
					},
				]
			: []),
		// 削除は選択中の全件に作用するため、複数選択中も表示する。
		{ label: "削除", testId: "file-node-menu-delete", onSelect: onDeleteSelected },
	];

	return (
		<div className="flex flex-col">
			{uploadInputs}
			<button
				onClick={handleClick}
				onDoubleClick={() => fileId !== null && openFile(fileId)}
				onContextMenu={handleContextMenu}
				onKeyDown={(event) => onRowKeyDown(event, entry.path)}
				tabIndex={isCurrent ? 0 : -1}
				data-path={entry.path}
				{...dragProps}
				{...dropProps}
				className={`relative flex w-full min-w-0 cursor-pointer items-center gap-x-1.5 px-2 py-0.5 text-left text-list focus-visible:outline focus-visible:outline-accent-solid focus-visible:[outline-offset:-1px] ${
					isOver ? TREE_ROW_DRAGOVER_CLASS : isSelected ? ROW_SELECTED_CLASS : ROW_HOVER_CLASS
				}`}
				style={{ paddingLeft: depth * TREE_INDENT_PX + TREE_ROOT_PADDING_PX }}
				data-testid={`file-node-${entry.name}`}
			>
				<TreeIndentGuides depth={depth} />
				{/* フォルダ名をファイル名と同じ位置に揃えるため、シェブロンもアイコンと同じ幅の枠に右寄せで置く。 */}
				<span className="flex w-4 shrink-0 justify-end">
					{isDir ? (
						<DisclosureChevron open={expanded} size={12} />
					) : (
						<FileIcon type={getFileIconType(entry.name)} />
					)}
				</span>
				<span
					className={`min-w-0 flex-1 truncate text-fg-1 ${isDir ? "font-medium" : ""}`}
					style={{ color: problemColor }}
				>
					{entry.name}
				</span>
				{loading && <Spinner data-testid={`file-node-loading-${entry.name}`} />}
				{totalProblems > 0 && (
					<span
						className="shrink-0"
						style={{
							fontFamily: "var(--font-mono)",
							fontSize: "var(--text-section)",
							color: problemColor,
						}}
					>
						{totalProblems}
					</span>
				)}
			</button>
			{menuPosition && (
				<ContextMenu
					position={menuPosition}
					onClose={() => setMenuPosition(null)}
					testId="file-node-menu"
					items={menuItems}
				/>
			)}
			<NewFolderDialog
				open={newFolderOpen}
				onConfirm={async (name) => {
					await createEntryInParent(createFolderInEnvironment, name);
					setNewFolderOpen(false);
				}}
				onCancel={() => setNewFolderOpen(false)}
			/>
			<NewFileDialog
				open={newFileOpen}
				onConfirm={async (name) => {
					await createEntryInParent(createFileInEnvironment, name);
					setNewFileOpen(false);
				}}
				onCancel={() => setNewFileOpen(false)}
			/>
		</div>
	);
}
